// =============================================================================
// io/sync/accounts.ts — load accounts, with their adjustments joined.
// -----------------------------------------------------------------------------
// The reader behind the Accounts tab, and behind every prop-compliance number:
// drawdown room, attempt number, consistency, payout eligibility.
//
// ── 🎯 THE HAZARD THAT SHAPES THIS FILE ──
//
// `adjustments` is a SEPARATE TABLE, and a failed read of it must fail the whole
// load rather than produce accounts with none.
//
// An adjustment of type "Reset fee" is what moves a prop account's compliance
// WINDOW: `attemptStart()` returns the latest reset date, `attemptNumber()`
// counts them. An account whose adjustments silently came back empty therefore
// reads as **attempt 1, starting from account creation, with the original
// drawdown** — a bigger cushion than the trader actually has, self-consistent,
// and wrong in the direction that gets someone blown out of an evaluation.
//
// That is the same shape as D47's Class-L problem and the same shape as the
// preferences "failed read becomes defaults" trap. It recurs because the safe
// value and the empty value look identical.
//
// ⚠ So there is deliberately no partial success here. Either both reads land or
// the caller gets an error.
//
// ── DERIVED SELECT ──
// The column list comes from ACCOUNT_COLUMNS / ACCOUNT_RULES_COLUMNS in domain/,
// which are exhaustive by type (`{ [K in keyof Account]-?: ... }`). A
// hand-written select drifts the moment a migration adds a column, and the row
// is then partial while looking complete.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import {
  ACCOUNT_COLUMNS, ACCOUNT_RULES_COLUMNS,
  type Account, type AccountAdjustment, type AccountId, type AccountRules,
  type AccountSource,
} from '../../domain/types';

/** Column names for the flat account fields (rules and adjustments excluded). */
const ACCOUNT_COLS = Object.values(ACCOUNT_COLUMNS).filter((c): c is string => !!c);

/**
 * Rules columns that actually exist.
 *
 * ⚠ `consistencyScope` and `dllEnforcement` map to null on purpose — they have
 * no column in the legacy schema and are the pair that vanished on reload in the
 * old client while two readers defaulted them OPPOSITELY. They are read from
 * `extras` below rather than dropped, so the type stays the contract.
 */
const RULES_COLS = Object.entries(ACCOUNT_RULES_COLUMNS)
  .filter((e): e is [string, string] => !!e[1]);

export const ACCOUNTS_SELECT = [...ACCOUNT_COLS, ...RULES_COLS.map(([, c]) => c), 'extras', 'created_at'].join(',');

const num = (v: unknown, fallback = 0): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
};

/** null stays null — "walk the trade timeline" is a real, different instruction
 *  from "the peak was zero". */
const numOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * The rules-provenance block, or null.
 *
 * ⚠ NOT a coercion. A bare string is REFUSED rather than wrapped, because the
 * shape that would actually appear is the literal `"[object Object]"` written
 * back by an earlier version of this loader — and wrapping that would promote a
 * bug into a stored URL. A block with no `url` is refused for the same reason:
 * the url is the only part anything would ever show.
 */
function accountSourceFromDb(v: unknown): AccountSource | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  const url = typeof o['url'] === 'string' ? o['url'].trim() : '';
  if (!url) return null;
  const out: AccountSource = {
    url,
    name: typeof o['name'] === 'string' ? o['name'] : null,
    // ⚠ Never defaulted to today. It records when a HUMAN last checked these
    // numbers against the firm's page, and null means nobody has.
    verifiedDate: typeof o['verifiedDate'] === 'string' ? o['verifiedDate'] : null,
  };
  if (typeof o['plan'] === 'string') out.plan = o['plan'];
  if (typeof o['templateId'] === 'string') out.templateId = o['templateId'];
  return out;
}

/** The forward-compat bag. Several real fields live here and nowhere else. */
function extrasOf(row: Record<string, unknown>): Record<string, unknown> {
  return (row['extras'] ?? {}) as Record<string, unknown>;
}

export function rulesFromDb(row: Record<string, unknown>): AccountRules {
  const extras = (row['extras'] ?? {}) as Record<string, unknown>;
  const rulesX = (extras['rulesX'] ?? {}) as Record<string, unknown>;

  const out: Record<string, unknown> = {};
  for (const [key, col] of Object.entries(ACCOUNT_RULES_COLUMNS)) {
    // An un-columned rule lives in extras.rulesX — the shim the old client added
    // after consistencyScope and dllEnforcement were dropped on every save.
    out[key] = col ? row[col] ?? null : rulesX[key] ?? null;
  }
  // Numeric rules that the domain treats as "a number, possibly 0" rather than
  // "unset". Left as-is when absent so a missing limit is not read as a limit
  // of zero, which would be the strictest possible rule instead of none.
  out['maxDDValue'] = num(out['maxDDValue']);
  out['dailyLossLimit'] = num(out['dailyLossLimit']);
  out['profitTarget'] = num(out['profitTarget']);
  return out as unknown as AccountRules;
}

export function adjustmentFromDb(row: Record<string, unknown>): AccountAdjustment {
  return {
    id: String(row['id'] ?? ''),
    date: String(row['date'] ?? '').slice(0, 10),
    type: String(row['type'] ?? ''),
    amount: num(row['amount']),
    note: String(row['note'] ?? ''),
  } as unknown as AccountAdjustment;
}

export function accountFromDb(
  row: Record<string, unknown>,
  adjustments: readonly AccountAdjustment[],
): Account {
  return {
    id: String(row['id'] ?? '') as AccountId,
    name: String(row['name'] ?? ''),
    firm: String(row['firm'] ?? ''),
    accountType: (row['account_type'] ?? 'personal') as Account['accountType'],
    accountSize: num(row['account_size']),
    startingBalance: num(row['starting_balance']),
    currentBalance: num(row['current_balance']),
    autoBalance: row['auto_balance'] === true,
    status: (row['status'] ?? 'active') as Account['status'],
    externalId: String(row['external_id'] ?? ''),
    // 🚨 A JSONB BLOCK, NOT A STRING. This was `String(row['source'])`, which
    // renders `{url, name, verifiedDate}` as the literal `"[object Object]"`.
    // Two of Josh's accounts carry a real one. Nothing read it and `saveAccount`
    // never writes it, so nothing was visibly wrong — but the "Set up account"
    // modal collects a firm rules URL and stores exactly this block.
    //
    // ⚠ A row whose `source` is a bare STRING is treated as absent rather than
    // coerced. There is no such row today, but "[object Object]" written back by
    // a previous version of this loader is the shape that would appear, and
    // wrapping that in `{ url: … }` would turn a bug into a stored URL.
    source: accountSourceFromDb(row['source']),
    initialPeakBalance: numOrNull(row['initial_peak_balance']),
    // ⚠ Read from `extras`, and NOT optional. The first version of this loader
    // omitted both, so every SIM and Replay account read as INCLUDED — and its
    // trades moved every number in the app. Josh has three such accounts and one
    // of them was a third of his weekly volume.
    //
    // The selector that honours the flag was tested and correct. The flag simply
    // never reached it — the test built the account object by hand, so it proved
    // the rule works and not that the data arrives. Same shape as D59.
    excludeFromMetrics: extrasOf(row)['excludeFromMetrics'] === true,
    archived: extrasOf(row)['archived'] === true,
    // ⚠ The cost fields, also from `extras` and for the same reason — the old
    // client keeps them out of `_ACCOUNT_KNOWN` so they have no column. The
    // Economics tab reads all three, and omitting them here would repeat exactly
    // the `excludeFromMetrics` failure above: a selector that honours the field,
    // tested and correct, that the data never reaches.
    // ⚠ THE SHIPPING CLIENT WRITES THIS ONE and, until #73, nothing here read
    // it — so a prompt the trader had already dismissed in beta reappeared on
    // every card. Both clients read one database; a flag only one of them
    // honours is a flag that does not work.
    setupNudgeDismissed: extrasOf(row)['setupNudgeDismissed'] === true,
    feeWaived: extrasOf(row)['feeWaived'] === true,
    feeCadence: String(extrasOf(row)['feeCadence'] ?? ''),
    feeAmount: num(extrasOf(row)['feeAmount']),
    rules: rulesFromDb(row),
    // Always an array. We DID query, so "none" is a fact rather than an absence
    // — but only because a failed adjustments read never reaches here.
    adjustments: [...adjustments],
    // `createdAt` is not on the Account type but attemptStart() reads it off
    // AccountLike, so it rides along.
    ...(row['created_at'] ? { createdAt: String(row['created_at']) } : {}),
  } as Account;
}

export interface LoadAccountsResult {
  accounts: Account[];
  /** Adjustment rows seen, for a caller that wants to report the join. */
  adjustmentCount: number;
}

/**
 * Load every account for a user, with adjustments attached.
 *
 * Throws if EITHER read fails. See the header: accounts-without-adjustments is
 * not a degraded answer, it is a wrong one that looks right.
 */
export async function loadAccounts(sb: SupabaseClient, userId: string): Promise<LoadAccountsResult> {
  const { data: rows, error } = await sb
    .from('accounts')
    .select(ACCOUNTS_SELECT)
    .eq('user_id', userId);

  if (error) throw new Error(`accounts read failed: ${error.message}`);
  const accountRows = (rows ?? []) as unknown as Record<string, unknown>[];

  // ⚠ Skipped only when there are NO accounts — not when there are accounts with
  // no adjustments, which is a query that must still run and succeed.
  if (!accountRows.length) return { accounts: [], adjustmentCount: 0 };

  const { data: adjRows, error: adjError } = await sb
    .from('account_adjustments')
    .select('id,account_id,date,type,amount,note')
    .eq('user_id', userId);

  if (adjError) {
    // NOT a partial success. An account missing its "Reset fee" rows reads as
    // attempt 1 with the original drawdown — more room than the trader has.
    throw new Error(`account adjustments read failed: ${adjError.message}`);
  }

  const byAccount = new Map<string, AccountAdjustment[]>();
  for (const r of (adjRows ?? []) as unknown as Record<string, unknown>[]) {
    const key = String(r['account_id'] ?? '');
    const list = byAccount.get(key) ?? [];
    list.push(adjustmentFromDb(r));
    byAccount.set(key, list);
  }

  return {
    accounts: accountRows.map((r) => accountFromDb(r, byAccount.get(String(r['id'] ?? '')) ?? [])),
    adjustmentCount: (adjRows ?? []).length,
  };
}
